import { useEffect, useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";

import { invalidateServerAuthGateState } from "../../environments/primary/auth";
import {
  checkWorkspaceAgain,
  probeWorkspace,
  retriesByItself,
  watchWorkspace,
  type WorkspaceRefusal,
  type WorkspaceUnavailableState,
  type WorkspaceWatch,
} from "../../environments/primary/workspaceState";
import type { I18nKey } from "~/i18n";
import { signOut } from "../../shared/auth";
import { openAfterAccountChange } from "../../shared/auth/web-address";
import { Button } from "../ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";

type PageState = Exclude<WorkspaceUnavailableState, "no_web_access">;

const TEXT: Record<PageState, { readonly title: I18nKey; readonly hint: I18nKey }> = {
  no_team: { title: "auth/youAreNotIn_n84q3w", hint: "auth/askAnOrganizationS_l5z3y4" },
  not_connected: {
    title: "auth/yourOrganizationSWeb_gge4mq",
    hint: "auth/askYourAdministratorTo_ubgvye",
  },
  stopped: { title: "auth/theWebWorkspaceIs_92hxdd", hint: "auth/askYourAdministratorTo_9c4xlw" },
  starting: { title: "auth/theWebWorkspaceIs_3gqft1", hint: "auth/thisPageOpensIt_yfpbcv" },
  outage: { title: "auth/theWebWorkspaceIs_14aw9u", hint: "auth/weWillTryAgain_hvjbyd" },
};

// A refusal the page itself received names a state it shows: `no_web_access`
// is the platform access page, which the root renders instead.
const pageState = (refusal: WorkspaceRefusal): PageState =>
  refusal.state === "no_team" ||
  refusal.state === "not_connected" ||
  refusal.state === "stopped" ||
  refusal.state === "starting"
    ? refusal.state
    : "outage";

/**
 * A workspace answering again, the session ending, and a lost access to the web
 * version are a fresh document's to show.
 */
const pageWatch = (onRefusal: (refusal: WorkspaceRefusal) => void): WorkspaceWatch => ({
  probe: () => probeWorkspace(),
  onRefusal: (refusal) =>
    refusal.state === "no_web_access" ? window.location.reload() : onRefusal(refusal),
  onSettled: () => window.location.reload(),
});

/**
 * The web address's page for a signed-in person whose session the router
 * routes to no workspace server. A person without a team and a workspace that
 * is not connected or stopped wait on an administrator, and the page asks
 * again every 30 s; a workspace that starts or is down passes, and the page
 * asks again after each `Retry-After`. Once a workspace answers, it loads the
 * app.
 */
export function WorkspaceStatePage({ refusal }: { readonly refusal: WorkspaceRefusal }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [current, setCurrent] = useState(refusal);
  const [checking, setChecking] = useState(false);
  const [signingOut, setSigningOut] = useState(false);
  const state = pageState(current);

  useEffect(
    () =>
      watchWorkspace(current, {
        ...pageWatch(setCurrent),
        setTimer: (run, delayMs) => {
          const timer = setTimeout(run, delayMs);
          return () => clearTimeout(timer);
        },
      }),
    [current],
  );

  return (
    <main className="flex min-h-screen items-center justify-center bg-background p-6">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle>{t(TEXT[state].title)}</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col gap-4">
          <p className="text-muted-foreground text-sm" role="status">
            {t(TEXT[state].hint)}
          </p>
          {retriesByItself(state) ? null : (
            <Button
              disabled={checking}
              onClick={() => {
                setChecking(true);
                void checkWorkspaceAgain(current, pageWatch(setCurrent)).finally(() =>
                  setChecking(false),
                );
              }}
            >
              {t("auth/checkAgain_4005u6")}
            </Button>
          )}
          <Button
            variant="outline"
            disabled={signingOut}
            onClick={() => {
              setSigningOut(true);
              void signOut().finally(() => {
                invalidateServerAuthGateState();
                openAfterAccountChange("/sign-in", (to) => void navigate({ to, replace: true }));
              });
            }}
          >
            {t("auth/signInWithAnother_7el4g2")}
          </Button>
        </CardContent>
      </Card>
    </main>
  );
}
